import React, { useRef, useState } from "react";

import { Box, Popover, Typography, useTheme } from "@mui/material";

import { ICON_SIZE } from "@dashboard/components/common/StreamlineIcon";
import { DropdownIcon } from "@dashboard/components/common/icons";

type FilterOption = {
  value: string;
  label: string;
};

type FilterDropdownProps = {
  options: FilterOption[];
  value: string;
  onChange: (value: string) => void;
  /** Used as both popover width hint and inline label style */
  minWidth?: number;
};

export const FilterDropdown = ({
  options,
  value,
  onChange,
  minWidth = 160,
}: FilterDropdownProps) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const open = Boolean(anchorEl);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? value;

  return (
    <>
      <Box
        ref={triggerRef}
        onClick={(e) => setAnchorEl(open ? null : e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          cursor: "pointer",
          userSelect: "none",
          minWidth,
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Typography
          noWrap
          sx={{
            // Without this the label refuses to shrink, pushing the mark past
            // the trigger's own overflow and clipping its ring.
            minWidth: 0,
            fontFamily: "Avenir",
            fontWeight: 500,
            fontSize: "14px",
            color: theme.palette.oscar.light.content.content1,
          }}
        >
          {selectedLabel}
        </Typography>
        {/* The ring is part of the mark now, not a border drawn around it. */}
        <DropdownIcon
          size={ICON_SIZE.medium}
          sx={{
            color: theme.palette.grey[700],
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: "16px",
              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
              minWidth,
              maxWidth: 300,
              p: "8px",
            },
          },
        }}
      >
        {options.map((option) => {
          const isSelected = option.value === value;
          return (
            <Box
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setAnchorEl(null);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                py: "8px",
                px: "8px",
                borderRadius: "8px",
                cursor: "pointer",
                userSelect: "none",
                transition: "background-color 120ms ease",
                "&:hover": {
                  bgcolor: theme.palette.oscar.light.surface.neutral,
                },
              }}
            >
              {/* Radio indicator */}
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  minWidth: 16,
                  borderRadius: "50%",
                  border: isSelected
                    ? `1.5px solid ${theme.palette.secondary.main}`
                    : `1.5px solid ${theme.palette.oscar.light.button.dark}`,
                  background: "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxSizing: "border-box",
                }}
              >
                {isSelected && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: theme.palette.secondary.main,
                    }}
                  />
                )}
              </Box>
              <Typography
                noWrap
                sx={{
                  fontFamily: "Avenir",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: theme.palette.oscar.light.content.content1,
                  minWidth: 0,
                }}
              >
                {option.label}
              </Typography>
            </Box>
          );
        })}
      </Popover>
    </>
  );
};
